import * as api from '@gitbook/api-client';
import {
    ADDON_TO_PRICING_FEATURE_KEY,
    compareSiteTypes,
    getBlockedOrganizationFeature,
    getRequiredPlanForSiteToUseMeter,
} from '@gitbook/app-pricing';
import { toast } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { useUpgradeDialogContext } from '../../Components';
import { apiClient } from '../api';
import { useTrial } from './useTrial';

type UseUpgradeAddonSubmitResult = { type: 'go-ahead' } | { type: 'checkout' };

type UseUpgradeAddonArgs = {
    organization: api.Organization;
    meterName: api.BillingMeter;
    onSuccess: () => void;
    site?: api.Site;
};

type UseUpgradeAddonResult = {
    onSubmit: () => Promise<void>;
    trialStatus: api.BillingTrialStatus;
    requiredSiteType?: api.SiteType;
    upgrade: boolean;
};

/**
 * Manage the upgrade flow required before enabling a billing add-on.
 *
 * Enabling an add-on may require multiple billing actions that must happen
 * in the correct order depending on the organization billing state and,
 * when relevant, the current site plan.
 *
 * For Free orgs:
 * - If a trial can be started, start it first.
 * - Otherwise, open the upgrade dialog, which carries the add-on and any
 *   required site upgrade in its initial state.
 *
 * For orgs with an active paid subscription:
 * - If the site already meets the add-on requirements, enable the add-on directly.
 * - If the site does not meet the requirements, upgrade the site first.
 * - If that site upgrade requires checkout, open the upgrade dialog instead.
 *
 * After all required upgrade steps succeed, enable the add-on.
 */
export function useUpgradeAddon(args: UseUpgradeAddonArgs): UseUpgradeAddonResult {
    const { organization, meterName, onSuccess, site } = args;
    const upgrade = Boolean(
        getBlockedOrganizationFeature(organization.plan, ADDON_TO_PRICING_FEATURE_KEY[meterName])
    );
    const trial = useTrial({ organization });
    const requiredSiteType = site
        ? getRequiredPlanForSiteToUseMeter({ meterName, site })
        : undefined;
    const { openUpgradeDialog } = useUpgradeDialogContext();
    const openAddonUpgradeDialog = useEventCallback(() => {
        openUpgradeDialog({
            organization,
            targetProduct: api.BillingProduct.Pro2024,
            reason: 'settings',
            onSuccess: (request) => {
                // If the meter was removed by the user, no need to enable it anymore.
                if (!request.addons?.includes(meterName)) {
                    return;
                }

                onSuccess();
            },
            initialState: {
                addons: new Set([meterName]),
                ...(site && requiredSiteType
                    ? {
                          siteTypeOverrides: new Map([
                              [
                                  site.id,
                                  {
                                      oldSiteType: site.type,
                                      newSiteType: requiredSiteType,
                                  },
                              ],
                          ]),
                      }
                    : {}),
            },
        });
    });

    // We must call preview and then update, because else updateSitePlan may
    // automatically create a checkout session with the wrong checkout payload.
    // E.g. a checkout for the site upgrade alone, instead of the one we want
    // with the addon as well.
    const previewSiteUpgradeIfRequired = useEventCallback(
        async (): Promise<UseUpgradeAddonSubmitResult> => {
            if (!site || !requiredSiteType) {
                return { type: 'go-ahead' };
            }

            const { data: preview } = await apiClient.sites.previewSitePlanUpdate(
                organization.id,
                site.id,
                { plan: requiredSiteType }
            );

            if (preview.type === 'checkout') {
                return { type: 'checkout' };
            }

            return { type: 'go-ahead' };
        }
    );

    const upgradeSiteIfRequired = useEventCallback(
        async (): Promise<UseUpgradeAddonSubmitResult> => {
            if (!site) {
                return { type: 'go-ahead' };
            }

            const currentSiteType = site.appliedType ?? site.type;

            if (
                !requiredSiteType ||
                !currentSiteType ||
                compareSiteTypes(requiredSiteType, currentSiteType) <= 0
            ) {
                return { type: 'go-ahead' };
            }

            const previewSiteUpgrade = await previewSiteUpgradeIfRequired();

            if (previewSiteUpgrade.type === 'checkout') {
                return { type: 'checkout' };
            }

            const { data: response } = await apiClient.sites.updateSitePlan(
                organization.id,
                site.id,
                {
                    plan: requiredSiteType,
                    reason: meterName,
                }
            );

            // Edge/worst case scenario: sub got canceled while the update was in flight.
            if (response.type === 'checkout') {
                return { type: 'checkout' };
            }

            return { type: 'go-ahead' };
        }
    );

    const onSubmit = useEventCallback(async () => {
        const shouldStartTrial = trial.status === api.BillingTrialStatus.None && upgrade;

        if (shouldStartTrial) {
            const didStartTrial = await toast.catch(async () => {
                await apiClient.billing.upgradeOrganizationPlan(organization.id, {
                    product: api.BillingProduct.Pro2024,
                    interval: api.BillingInterval.Monthly,
                    reason: 'upgrade_cta_trial',
                });

                return true;
            }, 'Error while starting trial');

            if (!didStartTrial) {
                return;
            }
        }

        if (upgrade && !shouldStartTrial) {
            openAddonUpgradeDialog();
            return;
        }

        const didEnableAddon = await toast.catch(async () => {
            const upgradeSite = await upgradeSiteIfRequired();

            if (upgradeSite.type === 'checkout') {
                openAddonUpgradeDialog();
                return false;
            }

            await apiClient.billing.enableOrganizationBillingMeterAddon(organization.id, meterName);

            return true;
        }, 'Error while enabling usage addon');

        if (!didEnableAddon) {
            return;
        }

        onSuccess();
    });

    return {
        onSubmit,
        trialStatus: trial.status,
        requiredSiteType,
        upgrade,
    };
}
